import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ForceGraph2D from 'react-force-graph-2d'
import { cn } from '@/lib/utils'
import { useTheme } from '../hooks/useTheme'
import type {
  NetworkGraphData,
  GraphNode,
  GraphLink,
  GraphNodeType,
  LinkSentiment,
} from '../../features/metrics/model/contracts'

interface NetworkGraphProps {
  data: NetworkGraphData
  heightMode?: 'compact' | 'full'
  highlightKeywords?: string[]
  onNodeClick?: (node: GraphNode) => void
}

function getThemeVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

function getSentimentColors() {
  return {
    link: {
      positive: getThemeVar('--sentiment-positive'),
      negative: getThemeVar('--sentiment-negative'),
      neutral: getThemeVar('--sentiment-neutral'),
    } as Record<LinkSentiment, string>,
    particle: {
      positive: getThemeVar('--sentiment-positive'),
      negative: getThemeVar('--sentiment-negative'),
      neutral: getThemeVar('--sentiment-neutral'),
    } as Record<LinkSentiment, string>,
  }
}

const SENTIMENT_OPTIONS: { value: LinkSentiment | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'positive', label: '✓' },
  { value: 'negative', label: '✗' },
  { value: 'neutral', label: '—' },
]

const NODE_SIZES: Record<GraphNodeType, { base: number; scale: number }> = {
  author: { base: 3, scale: 0.06 },
  source: { base: 5, scale: 0.08 },
  group: { base: 7, scale: 0.1 },
  party: { base: 8, scale: 0.14 },
  page: { base: 5, scale: 0.08 },
  topic: { base: 6, scale: 0.1 },
}

function nodeRadius(type: GraphNodeType, influence: number): number {
  if (type === 'author') return 4 + Math.sqrt(influence) * 1.5
  if (type === 'source') return 5 + Math.sqrt(influence) * 1.7
  if (type === 'group') return 7 + Math.sqrt(influence) * 2
  const size = NODE_SIZES[type]
  return size.base + (influence / 100) * size.scale * 40
}

export const NetworkGraph = memo(function NetworkGraph({
  data,
  heightMode = 'compact',
  highlightKeywords = [],
  onNodeClick,
}: NetworkGraphProps) {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-read CSS custom props when theme changes
  const sentimentColors = useMemo(() => getSentimentColors(), [theme])
  const prefersReducedMotion = useMemo(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 400, height: 400 })
  const [selectedNode, setSelectedNode] = useState<string | null>(null)
  const [hoveredNode, setHoveredNode] = useState<string | null>(null)
  const [activeGroups, setActiveGroups] = useState<Set<string>>(
    new Set(data.groups.map((g) => g.key)),
  )
  const [sentimentFilter, setSentimentFilter] = useState<LinkSentiment | 'all'>('all')

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const obs = new ResizeObserver(([entry]) => {
      const { width, height: containerH } = entry.contentRect
      const h =
        heightMode === 'full' ? Math.max(containerH, 400) : Math.min(width * 0.85, 500)
      setDimensions({ width, height: h })
    })
    obs.observe(el)
    return () => obs.disconnect()
  }, [heightMode])

  const filteredData = useMemo(() => {
    const nodes = data.nodes.filter((n) => activeGroups.has(n.group))
    const activeIds = new Set(nodes.map((n) => n.id))
    const links = data.links.filter((l) => {
      if (!activeIds.has(l.source) || !activeIds.has(l.target)) return false
      if (sentimentFilter !== 'all' && l.sentiment !== sentimentFilter) return false
      return true
    })
    return { nodes, links }
  }, [data, activeGroups, sentimentFilter])

  const connectedIds = useMemo(() => {
    const ids = new Set<string>()
    if (!selectedNode) return ids
    ids.add(selectedNode)
    filteredData.links.forEach((l) => {
      const src = typeof l.source === 'object' ? (l.source as GraphNode).id : l.source
      const tgt = typeof l.target === 'object' ? (l.target as GraphNode).id : l.target
      if (src === selectedNode) ids.add(tgt)
      if (tgt === selectedNode) ids.add(src)
    })
    return ids
  }, [selectedNode, filteredData.links])

  const keywordSet = useMemo(
    () => new Set(highlightKeywords.map((k) => k.toLowerCase())),
    [highlightKeywords],
  )

  const stateRef = useRef({
    selectedNode,
    hoveredNode,
    connectedIds,
    isDark,
    sentimentFilter,
    sentimentColors,
    keywordSet,
  })
  useEffect(() => {
    stateRef.current = {
      selectedNode,
      hoveredNode,
      connectedIds,
      isDark,
      sentimentFilter,
      sentimentColors,
      keywordSet,
    }
  }, [
    selectedNode,
    hoveredNode,
    connectedIds,
    isDark,
    sentimentFilter,
    sentimentColors,
    keywordSet,
  ])

  const toggleGroup = useCallback((key: string) => {
    setActiveGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        if (next.size > 1) next.delete(key) // keep at least one
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const paintNode = useCallback(
    (
      node: Record<string, unknown>,
      ctx: CanvasRenderingContext2D,
      globalScale: number,
    ) => {
      const {
        selectedNode: sel,
        hoveredNode: hov,
        connectedIds: conn,
        keywordSet: kws,
      } = stateRef.current
      const gn = node as unknown as GraphNode & { x: number; y: number }
      const r = nodeRadius(gn.type, gn.influence)
      const keywordDimmed = kws.size > 0 && !kws.has(gn.label.toLowerCase())
      const dimmed = keywordDimmed || (sel !== null && !conn.has(gn.id))

      ctx.globalAlpha = dimmed ? 0.3 : 1

      if (gn.type === 'author') {
        ctx.beginPath()
        ctx.arc(gn.x, gn.y, r, 0, Math.PI * 2)
        ctx.fillStyle = dimmed ? gn.color + '4d' : gn.color
        ctx.fill()
      } else if (gn.type === 'source') {
        ctx.beginPath()
        drawHexagon(ctx, gn.x, gn.y, r)
        ctx.fillStyle = dimmed ? gn.color + '4d' : gn.color
        ctx.fill()
      } else if (gn.type === 'group') {
        ctx.beginPath()
        drawHexagon(ctx, gn.x, gn.y, r)
        ctx.strokeStyle = dimmed ? gn.color + '4d' : gn.color
        ctx.lineWidth = 1.5
        ctx.stroke()
      } else if (gn.type === 'party') {
        ctx.beginPath()
        drawHexagon(ctx, gn.x, gn.y, r)
        ctx.fillStyle = dimmed ? gn.color + '40' : gn.color + 'cc'
        ctx.fill()
        ctx.strokeStyle = gn.color
        ctx.lineWidth = sel === gn.id || hov === gn.id ? 2 : 0.5
        ctx.stroke()
      } else {
        ctx.beginPath()
        if (gn.type === 'page') {
          const half = r * 0.85
          ctx.rect(gn.x - half, gn.y - half, half * 2, half * 2)
        } else {
          ctx.arc(gn.x, gn.y, r, 0, Math.PI * 2)
        }
        ctx.fillStyle = dimmed ? gn.color + '40' : gn.color + 'cc'
        ctx.fill()
        ctx.strokeStyle = gn.color
        ctx.lineWidth = sel === gn.id || hov === gn.id ? 2 : 0.5
        ctx.stroke()
      }

      if (hov === gn.id) {
        ctx.beginPath()
        ctx.arc(gn.x, gn.y, r + 2, 0, Math.PI * 2)
        ctx.strokeStyle = '#ffffff'
        ctx.lineWidth = 1
        ctx.stroke()
      }

      const showLabel =
        hov === gn.id || sel === gn.id || (sel !== null && conn.has(gn.id))
      if (showLabel) {
        const fontSize = Math.max(10 / globalScale, 3)
        ctx.font = `${gn.type === 'party' || gn.type === 'group' ? '600 ' : '500 '}${fontSize}px 'Inter', sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillStyle = getThemeVar('--text-1')
        ctx.fillText(gn.label, gn.x, gn.y + r + fontSize * 0.8)
      }
      ctx.globalAlpha = 1
    },
    [],
  )

  const paintLink = useCallback(
    (link: Record<string, unknown>, ctx: CanvasRenderingContext2D) => {
      const {
        selectedNode: sel,
        connectedIds: conn,
        sentimentColors: colors,
        keywordSet: kws,
      } = stateRef.current
      const l = link as unknown as GraphLink & {
        source: GraphNode & { x: number; y: number }
        target: GraphNode & { x: number; y: number }
      }
      if (!l.source.x || !l.target.x) return
      const keywordDimmed =
        kws.size > 0 &&
        !kws.has(l.source.label.toLowerCase()) &&
        !kws.has(l.target.label.toLowerCase())
      const dimmed =
        keywordDimmed || (sel != null && !conn.has(l.source.id) && !conn.has(l.target.id))
      const alpha = dimmed ? 0.03 : 0.25 + (l.weight / 10) * 0.15
      ctx.globalAlpha = alpha
      ctx.beginPath()
      ctx.moveTo(l.source.x, l.source.y)
      ctx.lineTo(l.target.x, l.target.y)
      ctx.strokeStyle = colors.link[l.sentiment ?? 'neutral']
      ctx.lineWidth = dimmed
        ? 0.2
        : l.sentiment === 'negative'
          ? 0.8 + (l.weight / 10) * 1.2
          : 0.3 + (l.weight / 10) * 0.8
      ctx.stroke()
      ctx.globalAlpha = 1
    },
    [],
  )

  const particleColor = useCallback((link: Record<string, unknown>) => {
    const l = link as unknown as GraphLink
    return stateRef.current.sentimentColors.particle[l.sentiment ?? 'neutral']
  }, [])

  const handleNodeClick = useCallback(
    (node: Record<string, unknown>) => {
      const gn = node as unknown as GraphNode
      setSelectedNode((prev) => (prev === gn.id ? null : gn.id))
      onNodeClick?.(gn)
    },
    [onNodeClick],
  )

  const handleBgClick = useCallback(() => {
    setSelectedNode(null)
  }, [])

  const handleNodeHover = useCallback((node: Record<string, unknown> | null) => {
    setHoveredNode(node ? (node as unknown as GraphNode).id : null)
  }, [])

  const totalAuthors = filteredData.nodes.filter((n) => n.type === 'author').length
  const totalLinks = filteredData.links.length
  const totalTopics = filteredData.nodes.filter((n) => n.type === 'topic').length

  // Mood index
  const posLinks = data.links.filter((l) => l.sentiment === 'positive').length
  const moodPct =
    data.links.length > 0 ? Math.round((posLinks / data.links.length) * 100) : 0

  return (
    <div className={cn('flex flex-col gap-3', heightMode === 'full' && 'h-full')}>
      {/* Controls row — horizontally scrollable on mobile */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none lg:flex-wrap lg:overflow-visible lg:pb-0">
        {/* Group filters */}
        {data.groups.map((g) => (
          <button
            key={g.key}
            type="button"
            onClick={() => toggleGroup(g.key)}
            className={cn(
              'flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full px-3 py-2 text-xs font-medium transition-all active:scale-95 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-none',
              activeGroups.has(g.key)
                ? 'ring-1 ring-current text-foreground'
                : 'text-muted-foreground opacity-50',
            )}
          >
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: g.color }}
            />
            {g.label}
            <span className="text-muted-foreground">({g.count})</span>
          </button>
        ))}

        {/* Sentiment filter */}
        <div className="flex shrink-0 items-center gap-1 rounded-full bg-secondary/30 p-0.5 ring-1 ring-primary/15">
          {SENTIMENT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSentimentFilter(opt.value)}
              className={cn(
                'min-h-[44px] rounded-full px-3 py-2 text-xs font-semibold transition-all active:scale-95 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-none',
                sentimentFilter === opt.value
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* KPI badges — hidden on mobile, shown on lg+ */}
        <div className="ml-auto hidden items-center gap-3 text-xs text-muted-foreground lg:flex">
          <span>
            {t('analytics.networkGraph.authors', { defaultValue: 'Authors' })}{' '}
            <b className="text-foreground">{totalAuthors}</b>
          </span>
          <span>
            {t('analytics.networkGraph.connections', { defaultValue: 'Connections' })}{' '}
            <b className="text-foreground">{totalLinks}</b>
          </span>
          <span>
            {t('analytics.networkGraph.topics', { defaultValue: 'Topics' })}{' '}
            <b className="text-foreground">{totalTopics}</b>
          </span>
        </div>
      </div>

      {/* KPI badges — visible only on mobile, below controls */}
      <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground lg:hidden">
        <span>
          {t('analytics.networkGraph.authors', { defaultValue: 'Authors' })}{' '}
          <b className="text-foreground">{totalAuthors}</b>
        </span>
        <span>
          {t('analytics.networkGraph.connections', { defaultValue: 'Connections' })}{' '}
          <b className="text-foreground">{totalLinks}</b>
        </span>
        <span>
          {t('analytics.networkGraph.topics', { defaultValue: 'Topics' })}{' '}
          <b className="text-foreground">{totalTopics}</b>
        </span>
      </div>

      {/* Graph container */}
      <div
        ref={containerRef}
        role="img"
        aria-label={t('analytics.networkGraph.aria', {
          defaultValue: 'Network graph showing {{nodes}} nodes and {{links}} connections',
          nodes: filteredData.nodes.length,
          links: filteredData.links.length,
        })}
        tabIndex={-1}
        className={cn(
          'relative min-h-[320px] overflow-hidden rounded-xl border lg:min-h-0',
          'border-primary/10 bg-muted shadow-[0_0_24px_-6px_var(--shadow-brand-highlight)]',
          heightMode === 'full' && 'min-h-0 flex-1',
        )}
      >
        {/* Visually-hidden summary for screen readers */}
        <div className="sr-only">
          {t('analytics.networkGraph.summary', {
            defaultValue: '{{authors}} authors, {{topics}} topics, {{links}} connections',
            authors: totalAuthors,
            topics: totalTopics,
            links: totalLinks,
          })}
        </div>
        <ForceGraph2D
          graphData={filteredData as { nodes: GraphNode[]; links: GraphLink[] }}
          width={dimensions.width}
          height={dimensions.height}
          nodeId="id"
          nodeCanvasObject={paintNode}
          nodePointerAreaPaint={(node, color, ctx) => {
            const gn = node as unknown as GraphNode & { x: number; y: number }
            const r = nodeRadius(gn.type, gn.influence)
            ctx.beginPath()
            ctx.arc(gn.x, gn.y, r + 8, 0, Math.PI * 2)
            ctx.fillStyle = color
            ctx.fill()
          }}
          linkCanvasObject={paintLink}
          linkDirectionalParticles={prefersReducedMotion ? 0 : 1}
          linkDirectionalParticleWidth={1.5}
          linkDirectionalParticleColor={particleColor}
          linkDirectionalParticleSpeed={0.003}
          d3AlphaDecay={0.02}
          d3VelocityDecay={0.3}
          cooldownTicks={200}
          onNodeClick={handleNodeClick}
          onNodeHover={handleNodeHover}
          onBackgroundClick={handleBgClick}
          enableNodeDrag
          enableZoomInteraction
          enablePanInteraction
          backgroundColor="transparent"
        />

        {/* Selected node tooltip */}
        {selectedNode && (
          <NodeTooltip
            node={filteredData.nodes.find((n) => n.id === selectedNode)}
            linkCount={connectedIds.size - 1}
            isDark={isDark}
          />
        )}

        {/* Mood index overlay */}
        <div
          className={cn(
            'absolute bottom-3 left-3 z-10 flex items-center gap-2 rounded-lg bg-card/90 px-2.5 py-1.5 text-xs ring-1 ring-primary/15',
          )}
        >
          <span className="text-muted-foreground">
            {t('analytics.mood', { defaultValue: 'Mood' })}
          </span>
          <span
            className={cn(
              'font-bold tabular-nums',
              moodPct >= 50 ? 'text-sentiment-positive' : 'text-sentiment-negative',
            )}
          >
            {moodPct}%
          </span>
          <span className="text-muted-foreground/60">
            {t('analytics.positive', { defaultValue: 'positive' })}
          </span>
        </div>
      </div>
    </div>
  )
})

/* ── Node Tooltip ── */

function NodeTooltip({
  node,
  linkCount,
}: {
  node: GraphNode | undefined
  linkCount: number
  isDark: boolean
}) {
  const { t } = useTranslation()
  if (!node) return null

  return (
    <div className="absolute left-3 top-3 z-10 rounded-lg bg-card/95 px-3 py-2 text-xs ring-1 ring-primary/15 shadow-md shadow-[var(--shadow-brand-highlight)]">
      <div className="flex items-center gap-2">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: node.color }}
        />
        <span className="font-semibold text-foreground">{node.label}</span>
      </div>
      <div className="mt-1 flex gap-3 text-muted-foreground">
        <span>
          {t(`analytics.networkGraph.nodeTypes.${node.type}`, {
            defaultValue: node.type,
          })}
        </span>
        <span>
          {t('analytics.networkGraph.influence', { defaultValue: 'Influence' })}:{' '}
          {node.influence}
        </span>
        <span>
          {t('analytics.networkGraph.connections', { defaultValue: 'Connections' })}:{' '}
          {linkCount}
        </span>
      </div>
    </div>
  )
}

/* ── Hexagon helper ── */

function drawHexagon(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 6
    const x = cx + r * Math.cos(angle)
    const y = cy + r * Math.sin(angle)
    if (i === 0) ctx.moveTo(x, y)
    else ctx.lineTo(x, y)
  }
  ctx.closePath()
}
